import { fetchBulkLabelPickupByUIDs, fetchSingleLabelPickupByUID } from '@/api';
import { ROUTE } from '@/config';
import { useRequestPickup } from '@/hooksApi';
import { isSlotSelectionRequired } from '@/components/PickupSlots/slotGate';
import useSlotSelectionStore from '@/stores/useSlotSelectionStore';
import { OrderColumn, OrderStatusTab, PreferredTiming } from '@/types';
import { downloadFile } from '@/utils';
import { message } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
// v2-task-12fix (H2): extracted to a shared module so the Orders-list
// per-row "Ship Now" flow (src/pages/orders/list.tsx) can reuse the exact
// same auto-advance-toast logic — see that module's docblock.
import { showAutoAdvanceToastIfAny } from './autoAdvanceToast';

interface useSubmitSingleOrderProps {
  activeTab: string;
  pickupId?: string;
  orderId: string;
  statusSlug?: string;
}

const useSubmitSingleOrder = ({
  activeTab,
  orderId,
  pickupId,
  statusSlug,
}: useSubmitSingleOrderProps) => {
  const { mutateAsync: requestPickup, isLoading: isLoadingRequestPickup } = useRequestPickup();
  const { openSlotSelection } = useSlotSelectionStore();
  const [shippingIds, setShippingIds] = useState<Set<string>>(() => new Set());

  const queryClient = useQueryClient();

  const isShippingThisOrder = shippingIds.has(orderId);
  const { t } = useTranslation();
  const navigation = useNavigate();

  const handleReadyToShip = useCallback(async () => {
    setShippingIds((prev) => {
      if (prev.has(orderId)) return prev;
      const next = new Set(prev);
      next.add(orderId);
      return next;
    });

    const onShipSuccess = () => {
      message.success(t('operation'));
      queryClient.invalidateQueries(['order', orderId]);
      queryClient.invalidateQueries(['order-count']);
    };

    try {
      const response = await requestPickup({
        // saved_pick_up_address_uid: pickupId || '',
        order_uids: [orderId ?? ''],
      });
      onShipSuccess();
      showAutoAdvanceToastIfAny(response, t);
    } catch (error) {
      if (isSlotSelectionRequired(error)) {
        openSlotSelection({
          onSelect: async (timeSlotCode: string, preferredTiming?: PreferredTiming | null) => {
            try {
              const response = await requestPickup({
                order_uids: [orderId ?? ''],
                time_slot_code: timeSlotCode,
                ...(preferredTiming !== undefined ? { preferred_timing: preferredTiming } : {}),
              });
              onShipSuccess();
              showAutoAdvanceToastIfAny(response, t);
            } catch (retryError) {
              // Non-gate errors are already toasted by handleErrorMessage
              // (via useSentryMutation). A repeat SLOT_SELECTION_REQUIRED
              // should not happen since we just sent a code — log it rather
              // than silently swallowing it or reopening the modal.
              if (isSlotSelectionRequired(retryError)) {
                console.error(retryError);
              }
            }
          },
        });
        return;
      }
      throw error;
    } finally {
      setShippingIds((prev) => {
        if (!prev.has(orderId)) return prev;
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  }, [orderId, queryClient, requestPickup, openSlotSelection, t]);

  const handleInProgress = async () => {
    await downloadFile(orderId, fetchSingleLabelPickupByUID, 'pickup-label.pdf');
  };

  const onSubmit = useCallback(() => {
    if (activeTab === OrderStatusTab.READY_TO_SHIP && statusSlug === 'new') {
      handleReadyToShip();
    } else {
      handleInProgress();
    }
  }, [activeTab, statusSlug, handleReadyToShip, handleInProgress]);

  return { onSubmit, LoadingRequestPickup: isShippingThisOrder || isLoadingRequestPickup };
};

export default useSubmitSingleOrder;
